import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { Button, Avatar, AvatarFallback } from '@leoclinic/ui';
import { useAppAuth } from '@/providers/use-app-auth';
import { getInitials } from '@leoclinic/utils';
import { useTheme } from '@/hooks/use-theme';
import { useNavigate } from 'react-router-dom';
import { USE_MOCK_DATA } from '@/config/app.config';
import { Moon, Sun, LogOut, Menu } from 'lucide-react';
import { useNotifications, useUnreadCount, useNotificationMutations, useTherapistsOnDuty } from '@leoclinic/hooks';
import { formatDateTime } from '@leoclinic/utils';

interface NavbarProps {
  onMenuClick?: () => void;
}

export function Navbar({ onMenuClick }: NavbarProps) {
  const { user, clinic, signOut } = useAppAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const { data: onDutyTherapists } = useTherapistsOnDuty(clinic?.id ?? null);
  const { data: notifications } = useNotifications(user?.id ?? null);
  const { data: unreadCount } = useUnreadCount(user?.id ?? null);
  const { markRead, markAllRead } = useNotificationMutations(user?.id ?? null);

  const onDutyLabel = useMemo(() => {
    if (USE_MOCK_DATA) return user?.full_name ?? 'Administrador';
    const names = (onDutyTherapists ?? []).map((t) => t.full_name);
    if (names.length === 0) return 'Sem terapeuta em serviço';
    if (names.length === 1) return names[0];
    return names.join(' · ');
  }, [onDutyTherapists, user?.full_name]);

  const avatarName = onDutyLabel !== 'Sem terapeuta em serviço' ? onDutyLabel : (user?.full_name ?? 'U');

  useEffect(() => {
    if (!notifOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setNotifOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [notifOpen]);

  const handleSignOut = async () => {
    if (!USE_MOCK_DATA) await signOut();
    navigate('/login');
  };

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-card px-4 md:px-6">
      <div className="flex min-w-0 items-center gap-2 md:gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 md:hidden"
          aria-label="Abrir menu"
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-foreground">{onDutyLabel}</p>
          <p className="truncate text-xs text-muted-foreground">Em serviço</p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        {USE_MOCK_DATA && (
          <span className="hidden sm:inline-flex rounded-full bg-accent/20 px-2.5 py-1 text-xs font-medium text-accent">
            Modo Demo
          </span>
        )}
        <div className="relative" ref={notifRef}>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Notificações"
            aria-expanded={notifOpen}
            onClick={() => setNotifOpen(!notifOpen)}
          >
            <Bell className="h-4 w-4" />
            {(unreadCount ?? 0) > 0 && (
              <span className="pointer-events-none absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] text-white">
                {unreadCount}
              </span>
            )}
          </Button>
          {notifOpen && (
            <>
              <div
                className="fixed inset-0 z-40 md:hidden"
                onClick={() => setNotifOpen(false)}
                aria-hidden
              />
              <div className="fixed left-1/2 top-[calc(4.5rem+env(safe-area-inset-top))] z-50 w-[min(calc(100vw-2rem-env(safe-area-inset-left)-env(safe-area-inset-right)),20rem)] -translate-x-1/2 rounded-xl border border-border bg-card shadow-xl max-h-[min(24rem,calc(100dvh-6rem-env(safe-area-inset-top)-env(safe-area-inset-bottom)))] overflow-y-auto md:absolute md:left-auto md:right-0 md:top-full md:mt-2 md:w-80 md:translate-x-0">
                <div className="flex items-center justify-between p-3 border-b border-border">
                  <span className="text-sm font-medium">Notificações</span>
                  <button type="button" className="text-xs text-primary" onClick={() => markAllRead()}>
                    Marcar todas lidas
                  </button>
                </div>
                {(notifications ?? []).length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">Sem notificações</p>
                ) : (
                  (notifications ?? []).map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      className={`w-full text-left p-3 border-b border-border hover:bg-muted/50 ${!n.is_read ? 'bg-primary/5' : ''}`}
                      onClick={() => {
                        markRead(n.id);
                        setNotifOpen(false);
                        navigate('/appointments');
                      }}
                    >
                      <p className="text-sm font-medium">{n.title}</p>
                      {n.body && <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>}
                      <p className="text-xs text-muted-foreground mt-1">{formatDateTime(n.created_at)}</p>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Alternar tema">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        <Avatar className="hidden h-8 w-8 sm:flex">
          <AvatarFallback className="text-xs">{getInitials(avatarName)}</AvatarFallback>
        </Avatar>
        <Button variant="ghost" size="icon" onClick={handleSignOut} aria-label="Terminar sessão">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}
